// An event's admin view (/events/:slug/admin) — admins only (ADMIN_USER_IDS
// on the API; anyone else gets a 403 and a sentence).
//
// Who joined, what each pooled and was handed, the pool with how often each
// seed went out, anyone still without a seed, and Assign. The copy buttons
// are the manual fallback: they're built from this page's own read, not from
// Assign, so if Assign ever fails the pool links are still one paste away
// from Discord.

import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, Loader2, Pause, Play, Shuffle } from "lucide-react";
import type {
  ApiAssignResult,
  ApiEventAdminView,
  ApiEventParticipantRow,
  ApiEventPoolSeed,
  EventStatus,
  SeedApiDetail,
} from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { ApiError } from "@web-shared/lib/api";
import { json, webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";
import { eventPath, seedPath } from "@/lib/ctas";
import { audioOf } from "@/lib/my-seeds";
import { AmbientGlow } from "@/components/AmbientGlow";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePlayer, useTrackStatus } from "../player";
import { useSession } from "../session";

const ADMIN_POLL_MS = 5_000;

/**
 * A Discord masked link: `[title](<url>)` shows the title as the link, and
 * the <…> stops Discord unfurling a preview card for every line.
 */
function seedLink(seed: ApiEventPoolSeed): string {
  const title = seed.title.replace(/([[\]])/g, "\\$1");
  return `[${title}](<${window.location.origin}${seedPath(seed.versionId)}>)`;
}

/** One line per participant, for Discord: `name: [seed](link)`. */
export function assignmentLines(participants: ApiEventParticipantRow[]): string {
  return participants
    .map((p) => `${p.user.username}: ${p.assignment ? seedLink(p.assignment) : "(no seed yet)"}`)
    .join("\n");
}

/** One line per pooled seed: `[seed](link) by producer`. */
export function poolLines(pool: ApiEventPoolSeed[]): string {
  return pool.map((p) => `${seedLink(p)} by ${p.producer.username}`).join("\n");
}

export function EventAdminPage() {
  const { slug = "" } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const admin = useQuery({
    queryKey: ["web", "event", slug, "admin", session?.user.id ?? ""],
    queryFn: () => webApi<ApiEventAdminView>(`/events/${encodeURIComponent(slug)}/admin`),
    retry: (count, err) => !(err instanceof ApiError && (err.status === 403 || err.status === 404)) && count < 1,
    refetchInterval: (q) => (q.state.data?.event.status === "closed" ? false : ADMIN_POLL_MS),
  });
  useDocumentTitle(admin.data ? `Admin · ${admin.data.event.title}` : "Event admin");

  const [busy, setBusy] = useState<"assign" | EventStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<ApiAssignResult | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["web", "event", slug] });

  const assign = () => {
    setBusy("assign");
    setError(null);
    webApi<ApiAssignResult>(`/events/${encodeURIComponent(slug)}/assign`, json("POST"))
      .then((r) => {
        setLastRun(r);
        return refresh();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Assign failed."))
      .finally(() => setBusy(null));
  };

  const setStatus = (status: EventStatus) => {
    setBusy(status);
    setError(null);
    webApi(`/events/${encodeURIComponent(slug)}`, json("PATCH", { status }))
      .then(refresh)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't change the status."))
      .finally(() => setBusy(null));
  };

  if (admin.isPending) {
    return (
      <div className="space-y-3">
        <AmbientGlow />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }
  if (admin.isError) {
    const status = admin.error instanceof ApiError ? admin.error.status : 0;
    return (
      <div className="py-16 text-center">
        <AmbientGlow />
        <p className="text-lg font-bold">
          {status === 403 ? "Admins only." : status === 404 ? "No such event." : "Couldn't load this event."}
        </p>
        <Button asChild className="mt-5 h-11 rounded-full px-6 font-bold">
          <Link to={status === 404 ? "/events" : eventPath(slug)}>
            <ArrowLeft className="size-4" /> {status === 404 ? "All events" : "Back to the event"}
          </Link>
        </Button>
      </div>
    );
  }

  const { event, participants, pool } = admin.data;
  const unassigned = participants.filter((p) => !p.assignment);

  return (
    <div className="space-y-5">
      <AmbientGlow />

      <Link
        to={eventPath(slug)}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-raised/60 px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" /> Event page
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-primary">
            <Shuffle className="size-3.5" /> Admin
          </div>
          <h1 className="truncate text-3xl font-black tracking-tight">{event.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <StatusPill status={event.status} /> · {participants.length} joined · {pool.length} in the pool ·{" "}
            <Link to="/events/admins" className="hover:text-foreground">
              All events
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {event.status !== "closed" && (
            <Button onClick={assign} disabled={busy !== null} className="rounded-full font-bold">
              {busy === "assign" ? <Loader2 className="size-4 animate-spin" /> : <Shuffle className="size-4" />}
              {event.status === "open" ? "Assign" : "Assign the rest"}
            </Button>
          )}
          {event.status === "closed" ? (
            <Button variant="outline" onClick={() => setStatus("assigned")} disabled={busy !== null} className="rounded-full">
              Reopen
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setStatus("closed")} disabled={busy !== null} className="rounded-full">
              End event
            </Button>
          )}
        </div>
      </header>

      {error && <p className="rounded-xl border border-removed/40 px-4 py-3 text-sm text-removed">{error}</p>}
      {lastRun && (
        <p className="rounded-xl border border-hairline bg-raised px-4 py-3 text-sm">
          Assigned {lastRun.assigned.length} {lastRun.assigned.length === 1 ? "person" : "people"}.
          {lastRun.unassigned.length > 0 &&
            ` No seed for ${lastRun.unassigned.map((u) => u.username).join(", ")} — pool another seed and Assign again.`}
        </p>
      )}

      {unassigned.length > 0 && event.status !== "open" && (
        <section className="rounded-2xl border border-dashed border-primary/30 bg-primary/[0.04] p-4">
          <h2 className="text-sm font-black">Without a seed · {unassigned.length}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{unassigned.map((p) => p.user.username).join(", ")}</p>
        </section>
      )}

      <div className="grid gap-5 lg:grid-cols-5">
        <section className="space-y-3 rounded-2xl border border-hairline bg-raised p-4 lg:col-span-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-black">Participants</h2>
            <CopyButton label="Copy for Discord" text={assignmentLines(participants)} disabled={participants.length === 0} />
          </div>
          {participants.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nobody yet.</p>
          ) : (
            <ul className="divide-y divide-hairline">
              {participants.map((p) => (
                <li key={p.user.id} className="grid gap-1 py-2.5 text-sm sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.2fr)] sm:gap-3">
                  <span className="truncate font-semibold">{p.user.username}</span>
                  <span className="truncate text-muted-foreground">
                    <span className="sm:hidden">Pooled: </span>
                    {p.pooled.length > 0 ? p.pooled.map((s) => s.title).join(", ") : "—"}
                  </span>
                  <span className="truncate">
                    <span className="text-muted-foreground sm:hidden">Got: </span>
                    {p.assignment ? (
                      <span className="inline-flex max-w-full items-center gap-1.5">
                        <PreviewButton seed={p.assignment} />
                        <Link to={seedPath(p.assignment.versionId)} className="truncate text-primary hover:underline">
                          {p.assignment.title}
                        </Link>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">no seed yet</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-3 rounded-2xl border border-hairline bg-raised p-4 lg:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-black">Pool</h2>
            <CopyButton label="Copy links" text={poolLines(pool)} disabled={pool.length === 0} />
          </div>
          {pool.length === 0 ? (
            <p className="text-sm text-muted-foreground">Empty. Pool a house seed from the event page.</p>
          ) : (
            <ul className="divide-y divide-hairline">
              {pool.map((p) => (
                <li key={p.poolItemId} className="flex items-center gap-3 py-2.5 text-sm">
                  <PreviewButton seed={p} />
                  <div className="min-w-0 flex-1">
                    <Link to={seedPath(p.versionId)} className="block truncate font-semibold hover:underline">
                      {p.title}
                    </Link>
                    <span className="block truncate text-xs text-muted-foreground">by {p.producer.username}</span>
                  </div>
                  <span
                    className={cn("shrink-0 tabular text-xs", p.assignedCount > 0 ? "text-primary" : "text-muted-foreground")}
                    title="Times assigned"
                  >
                    ×{p.assignedCount}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: EventStatus }) {
  const label = status === "open" ? "Open" : status === "assigned" ? "Assigned" : "Ended";
  return <span className={cn("font-semibold", status === "assigned" ? "text-primary" : "text-foreground")}>{label}</span>;
}

function CopyButton({ label, text, disabled }: { label: string; text: string; disabled?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button variant="outline" size="sm" onClick={copy} disabled={disabled} className="rounded-full">
      {copied ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
      {copied ? "Copied" : label}
    </Button>
  );
}

/**
 * ▶ a seed right here, through the app's player bar — the admin hears what's
 * in the pool without leaving the page. The seed's detail (fresh signed
 * URLs) is read on the first press.
 */
function PreviewButton({ seed }: { seed: ApiEventPoolSeed }) {
  const player = usePlayer();
  const status = useTrackStatus(seed.versionId);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const press = () => {
    if (status !== null && player.track) {
      player.toggle(player.track);
      return;
    }
    setLoading(true);
    setFailed(false);
    webApi<SeedApiDetail>(`/seeds/${encodeURIComponent(seed.versionId)}`)
      .then((detail) => {
        const url = audioOf(detail);
        if (!url) throw new Error("no audio");
        const handle = seed.producer.handle ?? "";
        player.toggle({
          id: seed.versionId,
          title: seed.title,
          url,
          producer: { handle, username: seed.producer.username },
          href: seedPath(seed.versionId),
        });
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  };
  const playing = status === "playing";
  return (
    <button
      type="button"
      onClick={press}
      aria-label={playing ? `Pause ${seed.title}` : `Play ${seed.title}`}
      title={failed ? "Couldn't play this one" : undefined}
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full transition-colors",
        playing ? "bg-primary text-primary-foreground" : "bg-white/10 text-foreground hover:bg-primary/20 hover:text-primary",
        failed && "text-removed",
      )}
    >
      {loading || status === "loading" ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : playing ? (
        <Pause className="size-3.5" />
      ) : (
        <Play className="ml-0.5 size-3.5" />
      )}
    </button>
  );
}
